import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import Chip from "components/Chips/Chip";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportSubCategoryCSV,
  fetchSubCategorys as OnfetchSubCategorys,
  updateStatusInSubCategory,
} from "slices/sub-category/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const SubCategory = () => {
  useEffect(() => {
    document.title = "SubCategory List";
  }, []);
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "subCategory",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const selectCategorys = createSelector(
    (state: any) => state.SubCategory,
    (category) => ({
      categories: category.subCategoryList,
      rowCount: category.rowCount,
      loading: category.loading,
      error: category.error,
    })
  );

  const { categories, rowCount, loading, error } = useSelector(selectCategorys);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.subcategory
  );

  const [data, setData] = useState([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      OnfetchSubCategorys({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
  ]);

  useEffect(() => {
    setData(categories);
  }, [categories]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Debounce search input to actual search
  useEffect(() => {
    // Skip if searchInput already matches search (prevents unnecessary updates during restoration)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  const handleStatusToggle = async (subCat: any) => {
    try {
      const updatedStatus = !subCat.is_active;
      const updatedSubCategory = {
        id: subCat.sub_category_id,
        is_active: updatedStatus,
      };

      setIsStatusUpdateLoading(true);
      await dispatch(updateStatusInSubCategory(updatedSubCategory)).unwrap();
      // await dispatch(updateStatusInSubCategory(updatedSubCategory)).unwrap();
      toast.success(
        `SubCategory ${
          updatedStatus ? "activated" : "deactivated"
        } successfully`
      );
      dispatch(
        OnfetchSubCategorys({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: colValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await exportSubCategoryCSV({
        search,
        filters: colValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction = canUpdate
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => (
            <EditButton
              to={`/edit-sub-category/${row?.original?.sub_category_id}`}
            />
          ),
        },
      ]
    : [];

  const columns =
    // useMemo(
    //   () =>
    [
      {
        header: "Sr. No.",
        id: "sr_no",
        accessorKey: "sr_no",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "Sub Category",
        accessorKey: "sub_category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "master/sub-category/dropdown/sub_category_name",
      },
      {
        header: "Categories",
        accessorKey: "category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "master/sub-category/dropdown/category.category_name",
        cell: ({ row }: { row: any }) => {
          const categories = row.original.categories || [];
          if (categories.length === 0) {
            return "-";
          }
          return (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
              }}
            >
              {categories.map((cat: any, index: number) => (
                <Chip>{cat.category_name || cat}</Chip>
                // <span
                //   key={cat.category_id || index}
                //   style={{
                //     backgroundColor: "#e3f2fd",
                //     color: "#1976d2",
                //     padding: "2px 8px",
                //     borderRadius: "4px",
                //     fontSize: "13px",
                //     marginRight: "4px",
                //   }}
                // >
                //   {cat.category_name || cat}
                // </span>
              ))}
            </div>
          );
        },
      },
      {
        header: "Remarks",
        accessorKey: "remark",
        enableSorting: false,
        enableColumnFilter: false,
        maxSize: 250,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remark}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remarks-tooltip-${row.index}`}
          />
        ),
      },

      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const pkg = row.original;
          return (
            <StatusToggle
              isActive={pkg.is_active}
              onChange={() => {
                handleStatusToggle(pkg);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },
      ...DynamicAction,
    ];
  //   [dispatch, categories]
  // );

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Sub Category</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Add Sub Category"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-sub-category"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <div className="">
                  <TableContainer
                    columns={columns}
                    data={data}
                    totalRecords={rowCount}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    columnFilters={columnFilters}
                    setColumnFilters={setColumnFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    isBordered={false}
                    isPagination={true}
                    isGlobalFilter={false}
                    SearchPlaceholder="Search categories..."
                    // columnFilters={[]}
                    // setColumnFilters={function (filters: ColumnFiltersState): void {
                    //   throw new Error("Function not implemented.");
                    // } }
                    manualSorting={true}
                    colValue={setColValue}
                  />
                </div>
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default SubCategory;
